/**
 * Predictions table component using TanStack Table
 * Displays paginated list of predictions with sorting and row click
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  OnChangeFn,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  NavigateBefore,
  NavigateNext,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { LessonV1_SpineTagsPrediction, Product, ExperimentVersion } from '../types';
import { StatusBadge } from './shared';
import InlineAssigneeDropdown from './InlineAssigneeDropdown';

interface PredictionsTableProps {
  predictions: LessonV1_SpineTagsPrediction[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  pagination: {
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  onPageChange: (direction: 'next' | 'prev') => void;
  product: Product;
  experiment: ExperimentVersion;
  availableAssignees: string[];
  s3PredictionIds: string[];
}

const columnHelper = createColumnHelper<LessonV1_SpineTagsPrediction>();

export default function PredictionsTable({
  predictions,
  sorting,
  onSortingChange,
  pagination,
  onPageChange,
  product,
  experiment,
  availableAssignees,
  s3PredictionIds,
}: PredictionsTableProps) {
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        cell: (info) => (
          <Tooltip title={info.getValue()}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
              {info.getValue()}
            </Typography>
          </Tooltip>
        ),
        size: 180,
      }),
      // Data indicator column
      columnHelper.display({
        id: 'data',
        header: 'Data',
        cell: (info) => {
          const prediction = info.row.original;
          const hasS3Data = s3PredictionIds.includes(prediction.id);
          
          return (
            <Tooltip title={hasS3Data ? 'Prediction data available' : 'Manifest only - no prediction data'}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {hasS3Data ? (
                  <CheckCircle sx={{ color: '#28a745', fontSize: 20 }} />
                ) : (
                  <Cancel sx={{ color: '#dc3545', fontSize: 20 }} />
                )}
              </Box>
            </Tooltip>
          );
        },
        size: 60,
      }),
      columnHelper.accessor('lesson_name', {
        header: 'Lesson Name',
        cell: (info) => info.getValue(),
        size: 240,
      }),
      columnHelper.accessor('lesson_grade', {
        header: 'Grade',
        cell: (info) => info.getValue(),
        size: 70,
      }),
      columnHelper.accessor('lesson_subject', {
        header: 'Subject',
        cell: (info) => {
          const subject = info.getValue();
          return <Typography variant="body2">{subject}</Typography>;
        },
        size: 100,
      }),
      columnHelper.accessor('unit_number', {
        header: 'Unit',
        cell: (info) => info.getValue(),
        size: 70,
      }),
      columnHelper.accessor('target_cc_standard_codes', {
        header: 'Target Standards',
        cell: (info) => {
          const codes = info.getValue();
          if (!codes || codes.length === 0) {
            return <Typography variant="body2">—</Typography>;
          }
          
          if (codes.length === 1) {
            return <Typography variant="body2">{codes[0]}</Typography>;
          }
          
          return (
            <Tooltip title={codes.join(', ')}>
              <Typography variant="body2" sx={{ cursor: 'help' }}>
                {codes[0]}… ({codes.length})
              </Typography>
            </Tooltip>
          );
        },
        size: 150,
      }),
      columnHelper.accessor('spine_tag_ids', {
        header: 'Tags',
        cell: (info) => {
          const tagIds = info.getValue();
          if (!tagIds || tagIds.length === 0) {
            return <Typography variant="body2">—</Typography>;
          }
          
          return (
            <Tooltip title={tagIds.join('\n')}>
              <Chip
                label={tagIds.length === 1 ? '1 tag' : `${tagIds.length} tags`}
                size="small"
                color="success"
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          );
        },
        size: 100,
      }),
      columnHelper.accessor('review_status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          return <StatusBadge status={status as any} />;
        },
        size: 120,
      }),
      columnHelper.accessor('reviewed_by', {
        header: 'Reviewers',
        cell: (info) => {
          const reviewers = info.getValue();
          if (!reviewers || reviewers.length === 0) {
            return <Typography variant="body2">None</Typography>;
          }
          
          if (reviewers.length === 1) {
            return (
              <Tooltip title={reviewers[0]}>
                <Typography variant="body2" noWrap sx={{ maxWidth: 120, cursor: 'help' }}>
                  {reviewers[0]}
                </Typography>
              </Tooltip>
            );
          }
          
          return (
            <Tooltip title={reviewers.join(', ')}>
              <Typography variant="body2" sx={{ cursor: 'help', color: '#007FFF' }}>
                {reviewers.length} reviewers
              </Typography>
            </Tooltip>
          );
        },
        size: 130,
      }),
      columnHelper.accessor('assignee', {
        header: 'Assignee',
        cell: (info) => {
          const prediction = info.row.original;
          return (
            <InlineAssigneeDropdown
              predictionId={prediction.id}
              product={product}
              experiment={experiment}
              currentAssignee={prediction.assignee}
              availableAssignees={availableAssignees}
            />
          );
        },
        size: 200,
      }),
    ],
    [product, experiment, availableAssignees, s3PredictionIds]
  );

  const table = useReactTable({
    data: predictions,
    columns,
    state: {
      sorting,
    },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
  });

  const handleRowClick = (predictionId: string) => {
    navigate(`/detail/${predictionId}`);
  };

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && (
                        <>
                          {header.column.getIsSorted() === 'asc' ? (
                            <ArrowUpward fontSize="small" />
                          ) : (
                            <ArrowDownward fontSize="small" />
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {predictions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                    No predictions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => handleRowClick(row.original.id)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          Page {pagination.currentPage}
        </Typography>
        <Box>
          <IconButton
            onClick={() => onPageChange('prev')}
            disabled={!pagination.hasPreviousPage}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
          <IconButton
            onClick={() => onPageChange('next')}
            disabled={!pagination.hasNextPage}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
